import * as Sentry from '@sentry/react';
import { env } from './env';

/**
 * Initialise Sentry once, before React mounts. No-ops when:
 *   - VITE_SENTRY_DSN is empty (dev / test / preview without a DSN);
 *   - import.meta.env.MODE === 'test' (vitest run — we don't want
 *     real network calls from the test suite even if a DSN leaked
 *     into the env via shell exports).
 *
 * The release tag is built from import.meta.env.MODE plus a short
 * hash injected at build time via Vite's `define` (see vite.config
 * — `__APP_VERSION__` falls back to 'dev' when not provided).
 *
 * `tracesSampleRate` is intentionally low (5 %) — performance traces
 * are noisy and the team is small; we want exception coverage first.
 */

declare const __APP_VERSION__: string;

let initialised = false;

export function initSentry(): void {
  if (initialised) return;
  if (!env.VITE_SENTRY_DSN) return;
  if (import.meta.env.MODE === 'test') return;
  initialised = true;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release:
      typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Off by default in prod — flip on per-incident via Sentry UI
        // sampleRate, never bake recording into the bundle wholesale.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Re-export the React ErrorBoundary so callers don't have to pull in
 * the Sentry namespace directly. When Sentry is not initialised it
 * still acts as a regular boundary that re-throws — the fallback is
 * the only thing the user sees.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
